import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { getAppStage } from "../utils/appStage";
import { useActiveSite } from "../context/ActiveSiteContext";

function formatPrice(amount, currency = "USD") {
  const value = Number(amount || 0);
  const resolvedCurrency = String(currency || "USD").toUpperCase();
  return new Intl.NumberFormat(resolvedCurrency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency: resolvedCurrency,
    maximumFractionDigits: 0,
  }).format(value);
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

export default function Pricing() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const { activeWorkspace, refreshSites } = useActiveSite();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingMode, setBillingMode] = useState("yearly");
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null, 'success', 'not_logged_in'

  const [currency, setCurrency] = useState("USD");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [targetPlan, setTargetPlan] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [submittingUpgrade, setSubmittingUpgrade] = useState(false);

  const stage = getAppStage();
  const isPreview = stage === "preview";

  const loadPlans = async (curr = "") => {
    setLoading(true);
    try {
      const { data } = await apiGet("/api/plans", { currency: curr || undefined });
      const plansList = Array.isArray(data?.plans) ? data.plans : [];
      setPlans(plansList);
      if (plansList.length > 0) {
        setCurrency(plansList[0].currency || "USD");
      }
    } catch (err) {
      showError(parseApiError(err, "Failed to load pricing details"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPreview) return;
    loadPlans("");
  }, [isPreview]);

  const effectivePlans = useMemo(() => {
    if (billingMode === "yearly") {
      return plans.map((plan) => {
        const normalizedId = String(plan.id || "").toLowerCase();
        if (normalizedId === "free") {
          return { ...plan, price_effective: 0, yearly_total: 0 };
        }
        // 15% off annual calculation
        const monthly = Number(plan.price_monthly || 0);
        const effectiveMonthly = Math.round(monthly * 0.85);
        const yearlyPrice = Math.round(monthly * 12 * 0.85);
        return { ...plan, price_effective: effectiveMonthly, yearly_total: yearlyPrice };
      });
    }
    return plans.map((plan) => ({ 
      ...plan, 
      price_effective: Number(plan.price_monthly || 0),
      yearly_total: Number(plan.price_monthly || 0) * 12
    }));
  }, [plans, billingMode]);

  const currentPlan = String(user?.plan_name || "free").toLowerCase();
  const freePlan = effectivePlans.find((p) => p.id === "free");
  const soloPlan = effectivePlans.find((p) => p.id === "solo");
  const agencyPlan = effectivePlans.find((p) => p.id === "agency");

  const handleJoinWaitlist = async () => {
    if (!isAuthenticated) {
      setWaitlistStatus("not_logged_in");
      return;
    }

    try {
      await apiPost("/api/newsletter/waitlist");
      setWaitlistStatus("success");
    } catch (err) {
      setWaitlistStatus("success");
    }
  };

  if (isPreview) {
    return (
      <div className="page page-pricing">
        <h1 className="page-title">Pricing & Plans</h1>
        <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
          We are currently in preview mode. Enjoy single anonymous SEO audits per 24 hours.
        </p>

        <div className="pricing-grid" style={{ gap: "2rem", marginTop: "2rem" }}>
          {/* Free Preview Card */}
          <div className="card pricing-card featured" style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "2rem", height: "100%" }}>
            <div className="pricing-card-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ fontSize: "1.5rem", margin: 0 }}>Free Preview</h2>
              <span className="pill-badge ok">Active</span>
            </div>
            <div className="pricing-price" style={{ fontSize: "2.5rem", fontWeight: "800" }}>
              $0<span style={{ fontSize: "1rem", color: "var(--text-muted)", fontWeight: "normal" }}>/during preview</span>
            </div>
            <ul className="plain-list" style={{ marginTop: 14, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "8px", flexGrow: 1 }}>
              <li>✓ 1 anonymous audit / 24 hours</li>
              <li>✓ Core SEO Score & Analysis</li>
              <li>✓ Detail breakdown & Share links</li>
              <li style={{ color: "var(--text-muted)", opacity: 0.75 }}>— PDF report downloads (disabled)</li>
              <li style={{ color: "var(--text-muted)", opacity: 0.75 }}>— Saved history / projects (disabled)</li>
            </ul>
            <div style={{ marginTop: 18 }}>
              <button type="button" className="btn btn-ghost" style={{ width: "100%", cursor: "default" }} disabled>
                Anonymous Preview Active
              </button>
            </div>
          </div>

          {/* Pro Card */}
          <div className="card pricing-card" style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "2rem", height: "100%", border: "1px dashed var(--border)" }}>
            <div className="pricing-card-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ fontSize: "1.5rem", margin: 0 }}>Pro (Coming Soon)</h2>
              <span className="pill-badge info">Waitlist</span>
            </div>
            <div className="pricing-price" style={{ fontSize: "2.5rem", fontWeight: "800" }}>
              TBA<span style={{ fontSize: "1rem", color: "var(--text-muted)", fontWeight: "normal" }}>/launch</span>
            </div>
            <ul className="plain-list" style={{ marginTop: 14, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "8px", flexGrow: 1 }}>
              <li>✓ Unlimited manual audits</li>
              <li>✓ Scheduled monitoring & Alerts</li>
              <li>✓ PDF report exports</li>
              <li>✓ Multi-workspace & team access</li>
              <li>✓ White-labeling & API</li>
            </ul>
            <div style={{ marginTop: 18, textAlign: "center" }}>
              <div style={{ fontSize: "0.85rem", color: "var(--warning)", marginBottom: "12px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "500" }}>
                Preview users lock in 30% off at launch
              </div>
              <button
                type="button"
                className="btn btn-warning"
                onClick={handleJoinWaitlist}
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: waitlistStatus === "success" ? "default" : "pointer"
                }}
              >
                {waitlistStatus === "success" ? "Joined Waitlist ✓" : "Join Waitlist"}
              </button>

              {waitlistStatus === "success" && (
                <div style={{
                  marginTop: "16px",
                  padding: "12px",
                  borderRadius: "8px",
                  background: "rgba(16, 185, 129, 0.1)",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                  color: "#10b981",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  textAlign: "center"
                }}>
                  🎉 You are on the waitlist! Your 30% launch discount is locked in. We'll email you updates!
                </div>
              )}

              {waitlistStatus === "not_logged_in" && (
                <div style={{
                  marginTop: "16px",
                  padding: "12px",
                  borderRadius: "8px",
                  background: "rgba(245, 158, 11, 0.1)",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                  color: "var(--warning)",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  textAlign: "center"
                }}>
                  🔑 Registration and login are disabled during preview. Please check back when public beta launches!
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 30, padding: "2rem" }}>
          <h3 className="page-subtitle" style={{ marginTop: 0, fontSize: "1.25rem" }}>Preview Feature Gating</h3>
          <div className="table-wrap" style={{ marginTop: "1rem" }}>
            <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th style={{ padding: "12px" }}>Feature</th>
                  <th style={{ padding: "12px" }}>Free Preview</th>
                  <th style={{ padding: "12px" }}>Pro (Launch)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px" }}>Audits limit</td>
                  <td style={{ padding: "12px" }}>1 anonymous audit / IP / 24 hours</td>
                  <td style={{ padding: "12px" }}>Unlimited</td>
                </tr>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px" }}>SEO Score & Recommendations</td>
                  <td style={{ padding: "12px" }}>Yes</td>
                  <td style={{ padding: "12px" }}>Yes</td>
                </tr>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px" }}>PDF Download</td>
                  <td style={{ padding: "12px" }}>No</td>
                  <td style={{ padding: "12px" }}>Yes</td>
                </tr>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px" }}>Scheduled Monitoring</td>
                  <td style={{ padding: "12px" }}>No</td>
                  <td style={{ padding: "12px" }}>Yes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-pricing">
      <h1 className="page-title">Simple Pricing for Growing Businesses</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        Choose a plan based on workflow, not complexity. Start free and upgrade when you need deeper audits.
      </p>



      <div className="pricing-billing-toggle card" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <h3 style={{ marginBottom: 4 }}>Billing Preference</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            Toggle currency or billing cycle to preview monthly or annual pricing.
          </p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {/* Currency Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label htmlFor="currency-select" style={{ fontSize: "0.9rem", fontWeight: "600", color: "var(--text-muted)" }}>Currency</label>
            <select
              id="currency-select"
              style={{
                padding: "6px 28px 6px 12px",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                background: "var(--bg-card)",
                color: "var(--text)",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: "pointer",
                appearance: "none",
                backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%2724%27%20height%3D%2724%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27none%27%20stroke%3D%27%23a0aec0%27%20stroke-width%3D%272%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%3E%3Cpolyline%20points%3D%276%209%2012%2015%2018%209%27%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
                backgroundSize: "16px"
              }}
              value={currency}
              onChange={(e) => {
                const val = e.target.value;
                setCurrency(val);
                loadPlans(val);
              }}
            >
              <option value="USD">$ USD</option>
              <option value="EUR">€ EUR</option>
              <option value="INR">₹ INR</option>
            </select>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="pricing-toggle-buttons">
            <button
              type="button"
              className={`btn btn-sm ${billingMode === "monthly" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setBillingMode("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`btn btn-sm ${billingMode === "yearly" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setBillingMode("yearly")}
            >
              Yearly (15% off)
            </button>
          </div>
        </div>
      </div>

      <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s", pointerEvents: loading ? "none" : "auto" }}>
        {loading && effectivePlans.length === 0 ? (
          <div className="card">
            <div className="loading">Loading pricing...</div>
          </div>
        ) : (
          <>
            <div className="pricing-grid" style={{ gap: "2rem", marginTop: "2rem" }}>
              {effectivePlans.map((plan) => {
                const isCurrent = isAuthenticated && currentPlan === String(plan.id || "").toLowerCase();
                const isFeatured = plan.id === "solo"; // Feature 'Solo' as the sweet spot
                return (
                  <div key={plan.id} className={`card pricing-card ${isFeatured ? "featured" : ""}`} style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "2rem", height: "100%" }}>
                    <div className="pricing-card-top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h2 style={{ fontSize: "1.5rem", margin: 0 }}>{plan.name}</h2>
                      {isCurrent ? <span className="pill-badge ok">Current Plan</span> : null}
                    </div>
                    <div className="pricing-price" style={{ fontSize: "2.5rem", fontWeight: "800" }}>
                      {formatPrice(plan.price_effective, plan.currency)}
                      <span style={{ fontSize: "1rem", color: "var(--text-muted)", fontWeight: "normal" }}>
                        /month
                      </span>
                    </div>
                    {billingMode === "yearly" && plan.price_effective > 0 && (
                      <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "-6px", fontWeight: "600" }}>
                        Billed annually ({formatPrice(plan.yearly_total, plan.currency)}/year)
                      </div>
                    )}
                    {(plan.id === "solo" || plan.id === "agency") && (
                      <div className="pricing-mobile-toggle-wrapper" style={{ marginTop: "8px", display: "none" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "0.85rem", fontWeight: "600", color: billingMode === "monthly" ? "var(--text)" : "var(--text-muted)" }}>Monthly</span>
                          <button
                            type="button"
                            onClick={() => setBillingMode(billingMode === "monthly" ? "yearly" : "monthly")}
                            style={{
                              width: "36px",
                              height: "20px",
                              borderRadius: "10px",
                              background: billingMode === "yearly" ? "var(--accent)" : "#cbd5e1",
                              border: "none",
                              position: "relative",
                              cursor: "pointer",
                              padding: 0,
                              transition: "background 0.2s"
                            }}
                          >
                            <span style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              background: "#fff",
                              position: "absolute",
                              top: "3px",
                              left: billingMode === "yearly" ? "19px" : "3px",
                              transition: "left 0.2s"
                            }} />
                          </button>
                          <span style={{ fontSize: "0.85rem", fontWeight: "600", color: billingMode === "yearly" ? "var(--accent)" : "var(--text-muted)" }}>Yearly (Save 15%)</span>
                        </div>
                      </div>
                    )}
                    <ul className="plain-list" style={{ marginTop: 14, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "8px", flexGrow: 1 }}>
                      {(plan.highlights || []).map((item) => {
                        const isNegative = item.toLowerCase().startsWith("no ") || 
                                           item.toLowerCase().startsWith("0 ") ||
                                           item.toLowerCase().includes("disabled") ||
                                           item.toLowerCase().includes("none");
                        return (
                          <li key={item} style={{ 
                            color: isNegative ? "var(--text-muted)" : "inherit",
                            opacity: isNegative ? 0.75 : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}>
                            <span style={{ 
                              color: isNegative ? "var(--text-muted)" : "#10b981", 
                              fontWeight: "bold",
                              fontSize: "1.1rem"
                            }}>
                              {isNegative ? "—" : "✓"}
                            </span>
                            <span>{item}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <div style={{ marginTop: 18 }}>
                      {!isAuthenticated ? (
                        <Link to="/login" className="btn btn-primary" style={{ display: "block", textAlign: "center" }}>
                          Start Free
                        </Link>
                      ) : isCurrent ? (
                        <button type="button" className="btn btn-ghost" style={{ width: "100%" }} disabled>
                          Active Plan
                        </button>
                      ) : activeWorkspace?.pending_upgrade_status === "pending" ? (
                        <button type="button" className="btn btn-ghost" style={{ width: "100%" }} disabled>
                          Request Pending...
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ width: "100%" }}
                          onClick={() => {
                            if (activeWorkspace?.role !== "owner") {
                              showError("Only the workspace owner can request plan upgrades.");
                              return;
                            }
                            setTargetPlan(plan);
                            setPaymentReference("");
                            setShowUpgradeModal(true);
                          }}
                        >
                          Request Upgrade
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card" style={{ marginTop: 30, padding: "2rem" }}>
              <h3 className="page-subtitle" style={{ marginTop: 0, fontSize: "1.25rem" }}>Full Feature Comparison</h3>
            <style>{`
              @media (max-width: 640px) {
                .pricing-comparison-desktop {
                  display: none !important;
                }
                .pricing-comparison-mobile {
                  display: block !important;
                }
                .pricing-mobile-toggle-wrapper {
                  display: block !important;
                }
              }
              @media (min-width: 641px) {
                .pricing-comparison-desktop {
                  display: block !important;
                }
                .pricing-comparison-mobile {
                  display: none !important;
                }
                .pricing-mobile-toggle-wrapper {
                  display: none !important;
                }
              }
            `}</style>

            <div className="table-wrap pricing-comparison-desktop" style={{ marginTop: "1rem" }}>
              <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "12px" }}>Feature / Limit</th>
                    <th style={{ padding: "12px" }}>Free</th>
                    <th style={{ padding: "12px" }}>Solo</th>
                    <th style={{ padding: "12px" }}>Agency</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Manual audits per month</td>
                    <td style={{ padding: "12px" }}>{freePlan?.limits?.manual_audits_per_month ?? 10}</td>
                    <td style={{ padding: "12px" }}>{soloPlan?.limits?.manual_audits_per_month ?? 50}</td>
                    <td style={{ padding: "12px" }}>
                      {agencyPlan?.limits?.manual_audits_per_month === 999999 ? "Unlimited" : (agencyPlan?.limits?.manual_audits_per_month ?? "Unlimited")}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Active projects</td>
                    <td style={{ padding: "12px" }}>{freePlan?.limits?.max_projects ?? 1}</td>
                    <td style={{ padding: "12px" }}>{soloPlan?.limits?.max_projects ?? 10}</td>
                    <td style={{ padding: "12px" }}>{agencyPlan?.limits?.max_projects ?? 30}</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Monitored sites</td>
                    <td style={{ padding: "12px" }}>{freePlan?.limits?.monitored_sites ?? 0}</td>
                    <td style={{ padding: "12px" }}>{soloPlan?.limits?.monitored_sites ?? 10}</td>
                    <td style={{ padding: "12px" }}>{agencyPlan?.limits?.monitored_sites ?? 30}</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Monitoring frequency</td>
                    <td style={{ padding: "12px" }}>—</td>
                    <td style={{ padding: "12px" }}>Weekly</td>
                    <td style={{ padding: "12px" }}>Daily + Weekly</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>PDF reports</td>
                    <td style={{ padding: "12px", color: "var(--text-muted)", fontSize: "1rem" }}>—</td>
                    <td style={{ padding: "12px", color: "#10b981", fontWeight: "bold", fontSize: "1.1rem" }}>✓</td>
                    <td style={{ padding: "12px" }}>
                      {agencyPlan?.limits?.can_white_label ? "White-label" : (agencyPlan?.limits?.can_export_pdf ? <span style={{ color: "#10b981", fontWeight: "bold", fontSize: "1.1rem" }}>✓</span> : <span style={{ color: "var(--text-muted)", fontSize: "1rem" }}>—</span>)}
                    </td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Keyword tracking</td>
                    <td style={{ padding: "12px" }}>{freePlan?.limits?.max_keywords ?? 0}</td>
                    <td style={{ padding: "12px" }}>{soloPlan?.limits?.max_keywords ?? 100}</td>
                    <td style={{ padding: "12px" }}>{agencyPlan?.limits?.max_keywords ?? 500}</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Team members</td>
                    <td style={{ padding: "12px" }}>{freePlan?.limits?.max_members ?? 1}</td>
                    <td style={{ padding: "12px" }}>{soloPlan?.limits?.max_members ?? 1}</td>
                    <td style={{ padding: "12px" }}>{agencyPlan?.limits?.max_members ?? 5}</td>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>Support</td>
                    <td style={{ padding: "12px" }}>None</td>
                    <td style={{ padding: "12px" }}>Email</td>
                    <td style={{ padding: "12px" }}>Priority</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="pricing-comparison-mobile" style={{ marginTop: "1rem" }}>
              {[
                {
                  label: "Manual audits per month",
                  free: freePlan?.limits?.manual_audits_per_month ?? 10,
                  solo: soloPlan?.limits?.manual_audits_per_month ?? 50,
                  agency: agencyPlan?.limits?.manual_audits_per_month === 999999 ? "Unlimited" : (agencyPlan?.limits?.manual_audits_per_month ?? "Unlimited")
                },
                {
                  label: "Active projects",
                  free: freePlan?.limits?.max_projects ?? 1,
                  solo: soloPlan?.limits?.max_projects ?? 10,
                  agency: agencyPlan?.limits?.max_projects ?? 30
                },
                {
                  label: "Monitored sites",
                  free: freePlan?.limits?.monitored_sites ?? 0,
                  solo: soloPlan?.limits?.monitored_sites ?? 10,
                  agency: agencyPlan?.limits?.monitored_sites ?? 30
                },
                {
                  label: "Monitoring frequency",
                  free: "—",
                  solo: "Weekly",
                  agency: "Daily + Weekly"
                },
                {
                  label: "PDF reports",
                  free: <span style={{ color: "var(--text-muted)", fontSize: "1rem" }}>—</span>,
                  solo: <span style={{ color: "#10b981", fontWeight: "bold", fontSize: "1.1rem" }}>✓</span>,
                  agency: agencyPlan?.limits?.can_white_label ? "White-label" : (agencyPlan?.limits?.can_export_pdf ? <span style={{ color: "#10b981", fontWeight: "bold", fontSize: "1.1rem" }}>✓</span> : <span style={{ color: "var(--text-muted)", fontSize: "1rem" }}>—</span>)
                },
                {
                  label: "Keyword tracking",
                  free: freePlan?.limits?.max_keywords ?? 0,
                  solo: soloPlan?.limits?.max_keywords ?? 100,
                  agency: agencyPlan?.limits?.max_keywords ?? 500
                },
                {
                  label: "Team members",
                  free: freePlan?.limits?.max_members ?? 1,
                  solo: soloPlan?.limits?.max_members ?? 1,
                  agency: agencyPlan?.limits?.max_members ?? 5
                },
                {
                  label: "Support",
                  free: "None",
                  solo: "Email",
                  agency: "Priority"
                }
              ].map((row, idx) => (
                <div 
                  key={idx} 
                  style={{ 
                    padding: "16px 8px", 
                    borderBottom: idx === 7 ? "none" : "1px solid var(--border)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px"
                  }}
                >
                  <div style={{ fontWeight: "600", color: "var(--text)", fontSize: "0.95rem" }}>
                    {row.label}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", background: "rgba(0,0,0,0.02)", padding: "8px 12px", borderRadius: "6px" }}>
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-muted)", fontWeight: "500", textTransform: "uppercase", marginBottom: "2px" }}>Free</span>
                      <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>{row.free}</span>
                    </div>
                    <div style={{ textAlign: "center", flex: 1, borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}>
                      <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-muted)", fontWeight: "500", textTransform: "uppercase", marginBottom: "2px" }}>Solo</span>
                      <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>{row.solo}</span>
                    </div>
                    <div style={{ textAlign: "center", flex: 1 }}>
                      <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-muted)", fontWeight: "500", textTransform: "uppercase", marginBottom: "2px" }}>Agency</span>
                      <span style={{ fontWeight: "600", fontSize: "0.9rem", color: "var(--accent)" }}>{row.agency}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
              
              <div style={{ marginTop: "2rem", borderTop: "1px solid var(--border)", paddingTop: "1.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "0.9rem", color: "var(--text-muted)" }}>
                  <p>💡 <strong>Usage-based limits:</strong> All limits are workspace-scoped and reset at the beginning of each monthly billing cycle.</p>
                  <p>🔄 <strong>Audits & schedules:</strong> Scheduled runs from automated monitoring do not count toward your monthly manual audit limits.</p>
                  {stage === "beta" && (
                    <p>✨ <strong>Beta Phase:</strong> Nova SEO is currently in Public Beta. Pricing, limits, and features listed above are from the Beta phase.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showUpgradeModal && targetPlan && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(15, 23, 42, 0.6)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "20px"
        }}>
          <div className="card" style={{ maxWidth: "480px", width: "100%", padding: "24px", position: "relative" }}>
            <h2 className="page-subtitle" style={{ marginTop: 0 }}>Request Upgrade to {targetPlan.name}</h2>
            <p style={{ fontSize: "0.88rem", color: "var(--text-muted)", marginBottom: 20 }}>
              You are requesting to upgrade the workspace <strong>{activeWorkspace?.name}</strong> to the <strong>{targetPlan.name}</strong> plan.
            </p>
            
            <div style={{ background: "var(--bg-subtle)", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span>Plan Price:</span>
                <strong>{formatPrice(targetPlan.price_effective, targetPlan.currency)}/{billingMode === "yearly" ? "year" : "month"}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Billing Cycle:</span>
                <strong style={{ textTransform: "capitalize" }}>{billingMode}</strong>
              </div>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setSubmittingUpgrade(true);
              try {
                await apiPost(`/api/workspaces/${activeWorkspace.id}/upgrade-request`, {
                  requestedPlan: targetPlan.id,
                  currency: targetPlan.currency,
                  price: targetPlan.price_effective,
                  paymentReference: paymentReference.trim()
                });
                showSuccess("Plan upgrade request submitted successfully! An admin will review it shortly.");
                setShowUpgradeModal(false);
                if (refreshSites) await refreshSites();
                if (refreshUser) await refreshUser();
              } catch (err) {
                showError(parseApiError(err, "Failed to submit upgrade request"));
              } finally {
                setSubmittingUpgrade(false);
              }
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Payment Reference / Receipt ID</label>
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. TXN-123456, Bank Transfer Ref"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  required
                />
                <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Provide the reference ID from your bank transfer or manual invoice payment.
                </span>
              </div>

              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowUpgradeModal(false)}
                  disabled={submittingUpgrade}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submittingUpgrade}
                >
                  {submittingUpgrade ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
